/**
 * useRebookBookingSubmit — the request half of the rebook drawer (BON-146): the
 * POST, what it returned or threw, and the notices derived from all of it.
 * Composes `useRebookFormState`, which owns what the CSR typed.
 *
 * Unlike the refund drawer, a 409 is NEVER quietly rewritten into a success
 * here. A refund that was already issued has the same outcome either way, so
 * showing the settled receipt is honest. A rebook that lost a race did NOT move
 * this customer to the workshop the CSR just chose — it moved them somewhere
 * else, earlier — so reporting the CSR's own intent back to them would be a lie
 * they would only discover on the customer's next phone call. The refusal links
 * to the successor instead.
 */
import { useMemo, useRef, useState } from 'react';

import { rebookBooking, type RebookBookingResult } from '../../api/rebooking';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';

import { deriveRebookNotices, isBlocked } from './rebookNotices';
import type { PickerWorkshop } from './shared/pickerWorkshop';
import { useRebookFormState } from './useRebookFormState';

export interface UseRebookBookingSubmitArgs {
  bookingId: string;
  /** Server verdict from the targets envelope; false until it loads. */
  cutoffPassed: boolean;
  canOverrideCutoff: boolean;
  /** Venue-local "Tue May 19 at 6:30p" for the workshop being left. */
  workshopWhen: string;
  onClose: () => void;
  /** Fires once the server confirms the move, so the page can re-read. */
  onRebooked: () => void;
  /**
   * Stop listening for Escape because the drawer is no longer the topmost
   * layer — the full-page calendar is open over it.
   *
   * Without this BOTH layers answer one keypress: the picker returns to the
   * form and the drawer closes behind it, throwing away the reason the CSR had
   * already typed. `useDialogKeyboard` binds to `window`, so there is no
   * bubbling to stop; the inner layer has to say when the outer one is muted.
   */
  escapeSuspended: boolean;
}

export function useRebookBookingSubmit(args: UseRebookBookingSubmitArgs) {
  const { bookingId, cutoffPassed, canOverrideCutoff, workshopWhen, onClose, onRebooked } = args;
  const { escapeSuspended } = args;
  const form = useRebookFormState();
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<RebookBookingResult | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  useDialogKeyboard(onClose, reasonRef, submitting || escapeSuspended);

  const notices = useMemo(
    () =>
      deriveRebookNotices({
        cutoffPassed,
        canOverrideCutoff,
        overrideChecked: form.overrideChecked,
        workshopWhen,
        attempted,
        hasPick: form.target !== null,
        error,
      }),
    [
      cutoffPassed,
      canOverrideCutoff,
      form.overrideChecked,
      workshopWhen,
      attempted,
      form.target,
      error,
    ],
  );

  const blocked = isBlocked(notices);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (blocked || submitting || !form.target) return;
    if (form.reason.trim().length === 0) {
      form.setReasonError('Please say why this booking is being moved.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await rebookBooking(bookingId, {
        targetWorkshopId: form.target.id,
        reason: form.reason.trim(),
        // Sent as the CSR's explicit request. The server decides whether it
        // actually counted — `overrideUsed` is derived there as
        // `requested && past cutoff`, so ticking early is a harmless no-op.
        overrideCutoff: form.overrideChecked,
      });
      onRebooked();
      setResult(res);
    } catch (err) {
      setError(err);
    } finally {
      // Always, not just on the error path: `submitting` suppresses Escape and
      // the backdrop click, both correct in flight and both wrong once the
      // receipt is on screen (the refund drawer had to learn this too).
      setSubmitting(false);
    }
  };

  return {
    ...form,
    /** A new destination invalidates the last refusal: NO_CAPACITY and the
     *  target-side refusals were about the OLD pick, and leaving one up tells
     *  the CSR their fresh choice is broken before they have tried it. */
    pickTarget: (workshop: PickerWorkshop) => form.pickTarget(workshop, () => setError(null)),
    submitting,
    notices,
    blocked,
    overriding: cutoffPassed && canOverrideCutoff && form.overrideChecked,
    result,
    reasonRef,
    submit,
    dismiss: () => {
      if (!submitting) onClose();
    },
  };
}
